import { LimeAlert } from "@/Components/NextBase/LimeAlert";
import { GlobalCustomerIndicator } from "@/Components/NextBase/Customer/GlobalCustomerIndicator";
import { LimeInput } from "@/Components/NextBase/LimeInput";
import { LimePhoneInput } from "@/Components/NextBase/LimePhoneInput";
import { LimeSwitch } from "@/Components/NextBase/LimeSwitch";
import { Language } from "@/server-types";
import { useForm } from "@mantine/form";
import { isValidPhoneNumber } from "react-phone-number-input";
import { t } from "@lingui/core/macro";
import { api } from "@/lib/api-client";
import { useEffect, useState } from "react";
import {
  Button,
  Divider,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Tabs,
  Tab,
  DateValue,
  ButtonGroup,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Link,
  addToast,
} from "@heroui/react";
import { Trans } from "@lingui/react/macro";
import { LimeModal } from "../LimeModal";
import { LimeTextarea } from "../LimeTextarea";
import { emailValid, numberAndCountryCodeToFullPhoneNumber } from "@/Utilities";
import { LimeCountryPicker } from "../LimeCountryPicker";
import { CompanyLookupInput } from "../CompanyLookupInput";
import { queryClient } from "@/lib/api-client";
import useAccountInfo from "@/hooks/useAccountInfo";
import { AlertButton } from "../AlertButton";
import { LimeLocalePicker } from "../LimeLocalePicker";
import { LimeDatePicker } from "../LimeDatePicker";
import {
  getLocalTimeZone,
  maxDate,
  minDate,
  parseDate,
  today,
} from "@internationalized/date";
import dayjs from "dayjs";
import { useClientFeature } from "@/hooks/useClientFeature";
import { ChevronDownIcon } from "lucide-react";
import { useNavigate } from "react-router";
import { useDebouncedValue } from "@mantine/hooks";
import { CompanySubscriptionsModal } from "./CompanySubscriptionsModal";

const EARLIEST_BIRTHDAY = parseDate("1900-01-01");

export type EntityType = "physical" | "legal";

export type CustomerPrefill = {
  name?: string;
  lastName?: string;
  email?: string;
};

export function shouldCheckEmailExistence(
  email: string | null | undefined,
): boolean {
  return email != null && email.length > 3 && emailValid(email);
}

export const ManageCustomerModal = ({
  isOpen,
  handleClose,
  editingEntityId,
  defaultEntityType,
  restrictToEntityType,
  showRedirectToCustomerCardButton = true,
  initialValues,
}: {
  isOpen: boolean;
  handleClose: ({
    customerId,
    companyId,
  }: {
    customerId?: number;
    companyId?: number;
  }) => void;
  editingEntityId?: number;
  defaultEntityType?: EntityType;
  restrictToEntityType?: EntityType;
  showRedirectToCustomerCardButton?: boolean;
  initialValues?: CustomerPrefill;
}) => {
  type PhysicalEntity = {
    personType: "physical";
    name: string;
    lastName: string;
    gsm: string | null | undefined;
    email: string;
    language: Language;
    notes: string;
    formBlocked: boolean;
    birthday: DateValue | null;
    isGsmConfirmed?: boolean;
    isEmailConfirmed?: boolean;
    labels: number[];
  };

  type LegalEntity = {
    personType: "legal";
    taxNumber: string;
    companyName: string;
    address: string;
    zipCode: string;
    city: string;
    country: string;
    isTaxSubject: boolean;
    language: Language;
    email: string;
    labels: number[];
  };

  type CustomerForm = PhysicalEntity | LegalEntity;

  const { isFeatureEnabled } = useClientFeature();

  const { accountInfo } = useAccountInfo();

  const navigate = useNavigate();

  const form = useForm<CustomerForm>({
    initialValues: {
      personType: defaultEntityType ?? "physical",
      name: "",
      lastName: "",
      gsm: undefined,
      email: "",
      taxNumber: "",
      companyName: "",
      address: "",
      zipCode: "",
      city: "",
      country: "",
      notes: "",
      isTaxSubject: false,
      language: (accountInfo?.preferred_language ?? "en") as Language,
      formBlocked: false,
      birthday: null,
      isGsmConfirmed: false,
      isEmailConfirmed: false,
      labels: [],
    },

    validate: {
      // physical entity validators
      name: (value, values) => {
        if (values.personType !== "physical") return;

        if (!value) return t`Obvezno polje`;
      },

      // legal entity validators
      companyName: (value, values) => {
        if (values.personType !== "legal") return;

        if (!value) return t`Obvezno polje`;
      },

      address: (value, values) => {
        if (values.personType !== "legal") return;

        if (!value) return t`Obvezno polje`;
      },

      email: (value, values) => {
        if (!value) return;

        if (!emailValid(value)) return t`Email je neveljaven`;
      },

      zipCode: (value, values) => {
        if (values.personType !== "legal") return;

        if (!value) return t`Obvezno polje`;
      },

      city: (value, values) => {
        if (values.personType !== "legal") return;

        if (!value) return t`Obvezno polje`;
      },

      country: (value, values) => {
        if (values.personType !== "legal") return;

        if (!value) return t`Obvezno polje`;
      },

      taxNumber: (value, values) => {
        if (values.personType !== "legal") return;

        if (!value) return t`Obvezno polje`;
      },
    },
  });

  // Determine the entity type for API calls - use defaultEntityType when editing, form values when creating
  const entityTypeForApi =
    editingEntityId != null
      ? (defaultEntityType ?? "physical")
      : form.getValues().personType;

  const { data: existingCustomerData, isFetching: isFetchingCustomer } =
    api.customer.useGetCustomer({
      customerId: entityTypeForApi === "physical" ? editingEntityId : undefined,
      enabled:
        isOpen && entityTypeForApi === "physical" && editingEntityId != null,
    });

  const { data: existingCompanyData, isFetching: isFetchingCompany } =
    api.company.useGetCompanies({
      companyId: entityTypeForApi === "legal" ? editingEntityId : undefined,
      enabled:
        isOpen && entityTypeForApi === "legal" && editingEntityId != null,
    });

  const isFetchingEntity =
    entityTypeForApi === "physical" ? isFetchingCustomer : isFetchingCompany;

  const [subscriptionsCompanyId, setSubscriptionsCompanyId] = useState<
    number | null
  >(null);

  // When the customer is linked to a marketplace (GlobalCustomer) account, that
  // account owns the identity/contact fields, so they are read-only here — the
  // server ignores any change to them on save (see putCustomer).
  const isGlobalCustomerManaged =
    entityTypeForApi === "physical" &&
    editingEntityId != null &&
    (existingCustomerData?.isGlobalCustomer ?? false);

  // Native validation would block saving the whole form on a stored birthday outside the bounds.
  const storedBirthday = existingCustomerData?.birthday
    ? parseDate(dayjs(existingCustomerData.birthday).format("YYYY-MM-DD"))
    : null;
  const birthdayMinValue = minDate(EARLIEST_BIRTHDAY, storedBirthday);
  const birthdayMaxValue = maxDate(today(getLocalTimeZone()), storedBirthday);

  useEffect(() => {
    if (
      entityTypeForApi !== "physical" ||
      !existingCustomerData ||
      !isOpen ||
      editingEntityId == null ||
      (restrictToEntityType != null && restrictToEntityType !== "physical")
    ) {
      return;
    }

    const customer = existingCustomerData;
    if (!customer) return;

    const fullGsm = numberAndCountryCodeToFullPhoneNumber(
      customer.gsm,
      customer.countryCode,
    );

    form.setValues({
      personType: "physical",
      ...customer,
      labels: customer.labels.map((label) => label.label.id),
      gsm:
        customer.gsmVisibility === "full" ? (fullGsm ?? undefined) : undefined,
      email:
        customer.emailVisibility === "full"
          ? (customer.email ?? undefined)
          : undefined,
      notes: customer.notes ?? undefined,
      formBlocked: customer.formBlocked ?? false,
      birthday: customer.birthday
        ? parseDate(dayjs(customer.birthday).format("YYYY-MM-DD"))
        : null,
      isGsmConfirmed: customer.isGsmConfirmed ?? false,
      isEmailConfirmed: customer.isEmailConfirmed ?? false,
    });
  }, [isOpen, existingCustomerData, editingEntityId, entityTypeForApi]);

  useEffect(() => {
    if (
      entityTypeForApi !== "legal" ||
      !existingCompanyData ||
      !isOpen ||
      editingEntityId == null ||
      (restrictToEntityType != null && restrictToEntityType !== "legal")
    ) {
      return;
    }

    const company = existingCompanyData?.companies?.[0];
    if (!company) return;

    form.setValues({
      personType: "legal",
      ...company,
      email: company.email ?? undefined,
    });
  }, [isOpen, existingCompanyData, editingEntityId, entityTypeForApi]);

  useEffect(() => {
    if (!isOpen) {
      form.reset();
      return;
    }

    if (isOpen) {
      if (defaultEntityType) {
        form.setFieldValue("personType", defaultEntityType);
      }
      if (initialValues && !editingEntityId) {
        form.setValues(initialValues);
      }
    }
  }, [isOpen]);

  const {
    mutateAsync: createCustomer,
    isPending: isCreatingCustomer,
    processedErrorMessage: createCustomerErrorMessage,
  } = api.customer.useAddCustomer();

  const {
    mutateAsync: updateCustomer,
    isPending: isUpdatingCustomer,
    processedErrorMessage: updateCustomerErrorMessage,
  } = api.customer.usePutCustomer();

  const {
    mutateAsync: createCompany,
    isPending: isCreatingCompany,
    processedErrorMessage: createCompanyErrorMessage,
  } = api.company.usePostCompany();

  const {
    mutateAsync: updateCompany,
    isPending: isUpdatingCompany,
    processedErrorMessage: updateCompanyErrorMessage,
  } = api.company.usePutCompany();

  const {
    mutateAsync: confirmCustomer2FA,
    isPending: isConfirmingCustomer2FA,
  } = api.customer.useConfirmCustomer2FA();

  const formValues = form.getValues();

  const [emailDebounced] = useDebouncedValue(formValues.email, 500);
  const shouldCheckCustomerEmail =
    editingEntityId == null &&
    formValues.personType === "physical" &&
    shouldCheckEmailExistence(emailDebounced);
  const shouldCheckCompanyEmail =
    editingEntityId == null &&
    formValues.personType === "legal" &&
    shouldCheckEmailExistence(emailDebounced);

  const {
    data: checkIfCustomerEmailExistsData,
    isFetching: isFetchingCheckIfCustomerEmailExists,
  } = api.customer.useGetCheckIfCustomerExists(
    {
      email: emailDebounced ?? undefined,
    },
    shouldCheckCustomerEmail,
  );

  const {
    data: checkIfCompanyEmailExistsData,
    isFetching: isFetchingCheckIfCompanyEmailExists,
  } = api.company.useGetCheckIfCompanyExists(
    {
      email: emailDebounced ?? undefined,
    },
    shouldCheckCompanyEmail,
  );

  const [customerGsmDebounced] = useDebouncedValue(
    formValues.personType === "physical" ? formValues.gsm : undefined,
    500,
  );
  const shouldCheckCustomerGsm =
    editingEntityId == null &&
    customerGsmDebounced != null &&
    customerGsmDebounced.length > 3 &&
    formValues.personType === "physical" &&
    isValidPhoneNumber(customerGsmDebounced);
  const {
    data: checkIfCustomerGsmExistsData,
    isFetching: isFetchingCheckIfCustomerGsmExists,
  } = api.customer.useGetCheckIfCustomerExists(
    {
      gsm: customerGsmDebounced!,
    },
    shouldCheckCustomerGsm,
  );

  // A disabled query still returns its cached result, so only trust the check that is live for this tab.
  const existingCustomerByEmail = shouldCheckCustomerEmail
    ? checkIfCustomerEmailExistsData
    : undefined;
  const existingCompanyByEmail = shouldCheckCompanyEmail
    ? checkIfCompanyEmailExistsData
    : undefined;
  const existingCustomerByGsm = shouldCheckCustomerGsm
    ? checkIfCustomerGsmExistsData
    : undefined;

  const isBlockedByDuplicateCheck =
    existingCustomerByGsm?.exists === true ||
    existingCustomerByEmail?.exists === true ||
    existingCompanyByEmail?.exists === true ||
    isFetchingCheckIfCustomerEmailExists ||
    isFetchingCheckIfCustomerGsmExists ||
    isFetchingCheckIfCompanyEmailExists;

  const handleSubmit = async (
    values: CustomerForm,
    shouldRedirectToCustomerCard?: boolean,
  ) => {
    if (values.personType === "physical") {
      const customerId = await (async () => {
        if (editingEntityId == null) {
          const response = await createCustomer({
            ...values,
            gsm: values.gsm ?? undefined,
            birthday: values.birthday ? values.birthday.toString() : undefined,
          });

          addToast({
            title: t`Stranka je bila uspešno ustvarjena`,
            color: "success",
          });
          queryClient.invalidateQueries({ queryKey: ["getCustomerList"] });

          return response.data;
        } else {
          const gsm = (() => {
            if (existingCustomerData?.gsmVisibility !== "full") {
              return undefined;
            }

            if (values.gsm === "") {
              return null;
            }

            return values.gsm ?? null;
          })();

          const email = (() => {
            if (existingCustomerData?.emailVisibility !== "full") {
              return undefined;
            }

            return values.email ?? undefined;
          })();

          await updateCustomer({
            customerId: editingEntityId,
            body: {
              ...values,
              gsm,
              email,
              birthday: values.birthday
                ? values.birthday.toString()
                : undefined,
            },
          });

          addToast({
            title: t`Stranka je bila uspešno posodobljena`,
            color: "success",
          });
          queryClient.invalidateQueries({ queryKey: ["getCustomerList"] });

          return editingEntityId;
        }
      })();

      handleClose({ customerId });

      if (shouldRedirectToCustomerCard) {
        navigate(`/dashboard/customers/${customerId}`);
      }
    } else if (values.personType === "legal") {
      const companyId = await (async () => {
        if (editingEntityId == null) {
          const response = await createCompany(values);

          addToast({
            title: t`Podjetje je bilo uspešno ustvarjeno`,
            color: "success",
          });

          return response.companyId;
        } else {
          await updateCompany({
            companyId: editingEntityId,
            body: values,
          });

          addToast({
            title: t`Podjetje je bilo uspešno posodobljeno`,
            color: "success",
          });

          return editingEntityId;
        }
      })();

      handleClose({ companyId });
    }
  };

  return (
    <>
      <LimeModal size="xl" isOpen={isOpen} onClose={() => handleClose({})}>
        <ModalContent>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();

              form.validate();

              if (form.isValid()) {
                handleSubmit(form.getValues());
              }
            }}
          >
            <ModalHeader className="flex-col">
              <div className="flex items-center gap-2">
                {editingEntityId != null ? (
                  <Trans>Uredi stranko</Trans>
                ) : (
                  <Trans>Dodaj novo stranko</Trans>
                )}
                {isGlobalCustomerManaged ? (
                  <GlobalCustomerIndicator size={18} />
                ) : null}
              </div>

              {editingEntityId != null ||
              restrictToEntityType != null ? undefined : (
                <div className="mt-8 mb-6 flex justify-center">
                  <Tabs
                    radius="full"
                    selectedKey={form.getValues().personType}
                    onSelectionChange={(type) =>
                      form.setFieldValue(
                        "personType",
                        type as CustomerForm["personType"],
                      )
                    }
                    disableAnimation
                  >
                    <Tab
                      key={"physical"}
                      title={t`Fizična oseba`}
                      className="px-4"
                    />
                    <Tab
                      key={"legal"}
                      title={t`Pravna oseba`}
                      className="px-4"
                    />
                  </Tabs>
                </div>
              )}
            </ModalHeader>
            <ModalBody>
              {formValues.personType === "physical" ? (
                <>
                  {isGlobalCustomerManaged && (
                    <LimeAlert
                      color="primary"
                      message={t`Ta stranka je povezana s svojim računom v platformi. Osebne podatke (ime, kontakt, jezik in datum rojstva) ureja stranka sama, zato jih tukaj ni mogoče urejati.`}
                    />
                  )}
                  <LimeInput
                    label={t`Ime`}
                    variant="bordered"
                    {...form.getInputProps("name")}
                    autoFocus
                    data-identifier="first-name"
                    isSkeleton={editingEntityId != null && isFetchingEntity}
                    isDisabled={isGlobalCustomerManaged}
                  />
                  <LimeInput
                    label={t`Priimek`}
                    variant="bordered"
                    {...form.getInputProps("lastName")}
                    data-identifier="last-name"
                    isSkeleton={editingEntityId != null && isFetchingEntity}
                    isDisabled={isGlobalCustomerManaged}
                  />

                  <LimePhoneInput
                    placeholder={
                      existingCustomerData?.gsmVisibility !== "full"
                        ? (existingCustomerData?.gsm ?? undefined)
                        : undefined
                    }
                    {...form.getInputProps("gsm")}
                    defaultCountry={accountInfo?.country ?? "SI"}
                    label={t`Telefon`}
                    error={
                      existingCustomerByGsm?.exists
                        ? existingError({
                            error: t`Stranka s to telefonsko številko že obstaja`,
                            customerId:
                              existingCustomerByGsm.customerId ?? undefined,
                          })
                        : formValues.gsm && !isValidPhoneNumber(formValues.gsm)
                          ? t`Telefonska številka ni veljavna`
                          : null
                    }
                    data-identifier="phone"
                    isSkeleton={editingEntityId != null && isFetchingEntity}
                    isLoading={isFetchingCheckIfCustomerGsmExists}
                    isDisabled={
                      isGlobalCustomerManaged ||
                      (editingEntityId != null &&
                        existingCustomerData?.gsm != null &&
                        existingCustomerData?.gsmVisibility !== "full")
                    }
                  />

                  <LimeInput
                    label={t`Email`}
                    placeholder={
                      existingCustomerData?.emailVisibility !== "full"
                        ? (existingCustomerData?.email ?? undefined)
                        : undefined
                    }
                    variant="bordered"
                    {...form.getInputProps("email")}
                    data-identifier="email"
                    isSkeleton={editingEntityId != null && isFetchingEntity}
                    isLoading={isFetchingCheckIfCustomerEmailExists}
                    isDisabled={
                      isGlobalCustomerManaged ||
                      (editingEntityId != null &&
                        existingCustomerData?.email != null &&
                        existingCustomerData?.emailVisibility !== "full")
                    }
                    error={
                      existingCustomerByEmail?.exists
                        ? existingError({
                            error: t`Stranka s tem email naslovom že obstaja`,
                            customerId:
                              existingCustomerByEmail.customerId ?? undefined,
                          })
                        : form.errors.email
                    }
                  />

                  <LimeTextarea
                    label={t`Komentar`}
                    variant="bordered"
                    {...form.getInputProps("notes")}
                    isLoading={editingEntityId != null && isFetchingEntity}
                  />

                  {isFeatureEnabled("twoFactorAuth") && (
                    <div className="flex flex-col gap-2">
                      {accountInfo.twoFactorAuthChannel === "sms" &&
                        formValues.gsm &&
                        editingEntityId != null && (
                          <>
                            {formValues.isGsmConfirmed ? null : (
                              <AlertButton
                                title={t`(2FA) Potrdi telefonsko številko`}
                                content={
                                  <Trans>
                                    Ali ste prepričani, da želite potrditi
                                    telefonsko številko?
                                  </Trans>
                                }
                                buttonProps={{
                                  variant: "bordered",
                                  isLoading: isConfirmingCustomer2FA,
                                }}
                                confirmProps={{
                                  onClick: async () => {
                                    if (!editingEntityId) return;

                                    await confirmCustomer2FA({
                                      customerId: editingEntityId,
                                      body: {
                                        confirmGsm: true,
                                        confirmEmail: false,
                                      },
                                    });

                                    form.setFieldValue("isGsmConfirmed", true);
                                  },
                                }}
                              >
                                <Trans>(2FA) Potrdi telefonsko številko</Trans>
                              </AlertButton>
                            )}
                          </>
                        )}
                      {accountInfo.twoFactorAuthChannel === "email" &&
                        formValues.email && (
                          <>
                            {formValues.isEmailConfirmed ? null : (
                              <AlertButton
                                title={t`(2FA) Potrdi elektronski naslov`}
                                content={
                                  <Trans>
                                    Ali ste prepričani, da želite potrditi
                                    elektronski naslov?
                                  </Trans>
                                }
                                buttonProps={{
                                  variant: "bordered",
                                  isLoading: isConfirmingCustomer2FA,
                                }}
                                confirmProps={{
                                  onClick: async () => {
                                    if (!editingEntityId) return;

                                    await confirmCustomer2FA({
                                      customerId: editingEntityId,
                                      body: {
                                        confirmGsm: false,
                                        confirmEmail: true,
                                      },
                                    });

                                    form.setFieldValue(
                                      "isEmailConfirmed",
                                      true,
                                    );
                                  },
                                }}
                              >
                                <Trans>(2FA) Potrdi elektronski naslov</Trans>
                              </AlertButton>
                            )}
                          </>
                        )}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <CompanyLookupInput
                    onLookupSuccess={(companyData) => {
                      form.setValues({
                        ...formValues,
                        companyName: companyData.name,
                        address: companyData.address,
                        zipCode: companyData.postalCode,
                        city: companyData.city,
                        isTaxSubject: companyData.isTaxSubject,
                      });
                    }}
                    taxNumber={formValues.taxNumber}
                    onValueChange={(taxNumber) => {
                      form.setFieldValue("taxNumber", taxNumber);
                    }}
                    error={form.errors.taxNumber}
                  />
                  <LimeInput
                    label={t`Ime podjetja`}
                    variant="bordered"
                    {...form.getInputProps("companyName")}
                    data-identifier="company-name"
                    isSkeleton={editingEntityId != null && isFetchingEntity}
                  />
                  <LimeInput
                    label={t`Naslov podjetja`}
                    variant="bordered"
                    {...form.getInputProps("address")}
                    data-identifier="company-address"
                    isSkeleton={editingEntityId != null && isFetchingEntity}
                  />
                  <LimeInput
                    label={t`Poštna številka podjetja`}
                    variant="bordered"
                    {...form.getInputProps("zipCode")}
                    data-identifier="company-zip"
                    isSkeleton={editingEntityId != null && isFetchingEntity}
                  />
                  <LimeInput
                    label={t`Kraj sedeža podjetja`}
                    variant="bordered"
                    {...form.getInputProps("city")}
                    data-identifier="company-city"
                    isSkeleton={editingEntityId != null && isFetchingEntity}
                  />
                  <LimeCountryPicker
                    label={t`Država sedeža podjetja`}
                    variant="bordered"
                    {...form.getInputProps("country")}
                    data-identifier="company-country"
                    isLoading={editingEntityId != null && isFetchingEntity}
                  />
                  <LimeInput
                    label={t`Email`}
                    variant="bordered"
                    {...form.getInputProps("email")}
                    data-identifier="company-email"
                    isSkeleton={editingEntityId != null && isFetchingEntity}
                    error={
                      existingCompanyByEmail?.exists
                        ? existingError({
                            error: t`Podjetje s tem email naslovom že obstaja`,
                            customerId:
                              existingCompanyByEmail.companyId ?? undefined,
                          })
                        : form.errors.email
                    }
                  />
                  <LimeSwitch
                    title={t`Davčni zavezanec`}
                    {...form.getInputProps("isTaxSubject", {
                      type: "checkbox",
                    })}
                    className="rounded-xl"
                    data-identifier="company-is-tax-subject"
                    isDisabled={editingEntityId != null && isFetchingEntity}
                  ></LimeSwitch>

                  {editingEntityId != null && (
                    <Button
                      variant="bordered"
                      onPress={() => setSubscriptionsCompanyId(editingEntityId)}
                      data-identifier="open-company-subscriptions-button"
                    >
                      <Trans>Sofinancirani paketi</Trans>
                    </Button>
                  )}
                </>
              )}

              <LimeLocalePicker
                {...form.getInputProps("language")}
                isLoading={isFetchingEntity}
                variant="bordered"
                isDisabled={isGlobalCustomerManaged}
              />

              {formValues.personType === "physical" && (
                <>
                  <LimeDatePicker
                    label={t`Datum rojstva`}
                    variant="bordered"
                    showMonthAndYearPickers
                    minValue={birthdayMinValue}
                    maxValue={birthdayMaxValue}
                    {...form.getInputProps("birthday")}
                    isDisabled={
                      isGlobalCustomerManaged ||
                      (editingEntityId != null && isFetchingEntity)
                    }
                  />

                  <LimeSwitch
                    title={t`Blokiraj stranko za spletno naročanje`}
                    {...form.getInputProps("formBlocked", {
                      type: "checkbox",
                    })}
                  />
                </>
              )}

              {createCustomerErrorMessage && (
                <LimeAlert
                  color="danger"
                  message={createCustomerErrorMessage}
                />
              )}

              {createCompanyErrorMessage && (
                <LimeAlert color="danger" message={createCompanyErrorMessage} />
              )}
            </ModalBody>
            <ModalFooter className="flex-col p-2">
              <Divider className="mt-2" />

              <LimeAlert
                color="danger"
                message={
                  updateCustomerErrorMessage || updateCompanyErrorMessage
                }
                className="my-2"
              />

              <div className="flex w-full gap-2">
                <Button
                  variant="bordered"
                  className="h-12 w-full"
                  onPress={() => handleClose({})}
                >
                  <Trans>Prekliči</Trans>
                </Button>

                <ButtonGroup className="w-full">
                  <Button
                    type="submit"
                    className="h-12 w-full"
                    color="primary"
                    isLoading={
                      isCreatingCustomer ||
                      isCreatingCompany ||
                      isUpdatingCustomer ||
                      isUpdatingCompany
                    }
                    isDisabled={isBlockedByDuplicateCheck}
                    data-identifier="confirm-button"
                  >
                    {editingEntityId == null ? (
                      <Trans>Dodaj novo stranko</Trans>
                    ) : (
                      <Trans>Uredi stranko</Trans>
                    )}
                  </Button>

                  {editingEntityId == null &&
                  form.getValues().personType === "physical" &&
                  showRedirectToCustomerCardButton ? (
                    <Dropdown
                      placement="bottom-end"
                      showArrow
                      isDisabled={isBlockedByDuplicateCheck}
                    >
                      <DropdownTrigger>
                        <Button className="h-12" isIconOnly color="primary">
                          <ChevronDownIcon />
                        </Button>
                      </DropdownTrigger>
                      <DropdownMenu
                        disallowEmptySelection
                        aria-label="Merge options"
                        selectionMode="single"
                        onAction={(key) => {
                          form.validate();

                          if (!form.isValid()) {
                            return;
                          }

                          if (key === "create-and-redirect-to-customer-card") {
                            handleSubmit(form.getValues(), true);
                          }
                        }}
                        disabledKeys={
                          isCreatingCustomer || isCreatingCompany
                            ? ["create-and-redirect-to-customer-card"]
                            : []
                        }
                      >
                        <DropdownItem key="create-and-redirect-to-customer-card">{t`Dodaj novo stranko in odpri kartico stranke`}</DropdownItem>
                      </DropdownMenu>
                    </Dropdown>
                  ) : null}
                </ButtonGroup>
              </div>
            </ModalFooter>
          </form>
        </ModalContent>
      </LimeModal>

      {/* After the edit modal, so its portal mounts on top of it. */}
      <CompanySubscriptionsModal
        companyId={subscriptionsCompanyId}
        onClose={() => setSubscriptionsCompanyId(null)}
      />
    </>
  );
};

const existingError = ({
  error,
  customerId,
}: {
  error: string;
  customerId?: number;
}) => {
  return (
    <div className="text-danger-500 text-xs">
      <span>{error}</span>
      {" | "}
      {customerId && (
        <Link
          href={`/dashboard/customers/${customerId}`}
          className="text-danger-500 text-xs"
          showAnchorIcon
        >
          <Trans>odpri stranko</Trans>
        </Link>
      )}
    </div>
  );
};
